import React, { useState } from 'react'
import { View, StyleSheet, TouchableOpacity } from 'react-native'
import { Text } from '@ui-kitten/components'
import MoneyTextInput from './MoneyTextInput'
import { finalTotalCartPrice } from './CartView'
import { useColors } from '../utils/useColors'
import { useTranslation } from 'react-i18next'
import { currencyAtom, currencyFormatterAtom } from '../global/AtomsProvider'
import { useAtomValue } from 'jotai'
import { AMOUNTS } from '../utils/currencyAmount'
import { addDecimalAmounts, parseDecimalAmount } from '../utils/decimalInput'

export const ChangeView = React.memo(function ChangeView(props) {
  const { t } = useTranslation()
  const colors = useColors()
  const currencyFormatter = useAtomValue(currencyFormatterAtom)
  const currency = useAtomValue(currencyAtom);
  const [changeValue, setChangeValue] = useState('')


  const addToChangeValue = (value) => {
    setChangeValue(changeValue => {
      // ATL-1371 — `parseInt` threw away the fraction of whatever was already typed, so tapping a
      // quick-amount button after entering 36.5 tendered 1036 instead of 1036.5.
      //
      // The result is a STRING, and that is not cosmetic: this input is `controlledInput`, and RN
      // only pushes `props.value` into the native view when it is a string
      // (`TextInput.js:1044`). Storing the number meant the chips updated the change LABEL while
      // the box the cashier is reading kept its old text — a pre-existing bug, but one that this
      // fix would otherwise have carried forward untouched.
      //
      // `addDecimalAmounts` rather than `+` because `0.14 + 1` is `1.1400000000000001`, which a
      // controlled box renders verbatim. `parseInt` used to make that impossible by truncating.
      const next = addDecimalAmounts(parseDecimalAmount(changeValue) ?? 0, value)
      // `addDecimalAmounts` returns null when it cannot add (non-finite), and `null.toString()`
      // would crash the checkout sheet. Unreachable from a chip today — both operands are finite
      // by construction — which is exactly why it is worth not leaving as a trap: leave the box
      // as the cashier typed it.
      return next === null ? changeValue : next.toString()
    })
  }

  // `''`, not `0`: the red X means "nothing tendered yet", and 0 is a tendered amount — it made
  // `changeDue` render the negative of the whole cart total instead of the placeholder.
  const clearValue = () => {
    setChangeValue('')
  }

  const priceTypeId = props.selectedPriceType?.id || null
  const tendered = parseDecimalAmount(changeValue)
  // An empty or unreadable box must keep showing 0, exactly as `(NaN - total) || 0` used to. It is
  // spelled out because `null - total` is NOT NaN — it coerces to 0 and would render the NEGATIVE
  // of the cart total as the change due.
  const changeDue = tendered === null
    ? 0
    : (tendered - finalTotalCartPrice(props.discountOptions[props.discountSelectedIndex]?.percentage, props.discountAmount, props.cartItems, props.store, props.client, props.bonusAction, props.cashbackEnabled, props.punchCardEnabled, props.punchCardCapacity, props.priceModificators, priceTypeId, props.priceTypes)) || 0
  
  return (
    <View style={props.style}>
      <Text category='label'>
        { t('changeView.change') + ': ' + currencyFormatter.format(changeDue) }
      </Text>
      <MoneyTextInput
        style={styles.changeInputView}
        value={changeValue}
        onChangeText={text => {
          setChangeValue(text)
        }}
        controlledInput={true}
        placeholder={t('changeView.otherValue')}
        returnKeyType='done'
        selectTextOnFocus={true}
      />
      <View style={{flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap'}}>
        {AMOUNTS[currency.id.toUpperCase()].map(amount => (
          <TouchableOpacity key={amount} style={[styles.button, {color: colors.primary, borderColor: colors.primary}]} onPress={() => addToChangeValue(amount)}><Text>{amount}</Text></TouchableOpacity>
        ))}
       <TouchableOpacity style={[styles.button, {color: colors.red, borderColor: colors.red}]} onPress={clearValue}><Text>X</Text></TouchableOpacity>
      </View>
    </View>
  )
}, function areEqual(prevProps, nextProps) {
  /*
  return true if passing nextProps to render would return
  the same result as passing prevProps to render,
  otherwise return false
  */
  if (prevProps.cartItems?.length !== nextProps.cartItems?.length) {
    return false
  }
  if (prevProps.discountSelectedIndex !== nextProps.discountSelectedIndex) {
    return false
  }
  if (prevProps.discountAmount !== nextProps.discountAmount) {
    return false
  }
  if (prevProps.bonusAction !== nextProps.bonusAction) {
    return false
  }
  if (prevProps.client !== nextProps.client) {
    return false
  }
  if (prevProps.discountOptions?.length !== nextProps.discountOptions?.length) {
    return false
  }
  for (let index = 0; index < prevProps.cartItems?.length; index++) {
    const prevElement = prevProps.cartItems[index]
    const nextElement = nextProps.cartItems[index]
    if (prevElement.quantity !== nextElement.quantity) {
      return false
    }
    if (prevElement.salePrices?.[prevProps.store] !== nextElement.salePrices?.[nextProps.store]) {
      return false
    }
  }
  return true
})


const styles = StyleSheet.create({
  changeView: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 10,
    marginTop: 5,
  },
  changeInputView: {
    marginBottom: 10,
    marginTop: 5,
  },
  button: {
    marginRight: 5,
    marginBottom: 5,
    borderRadius: 4,
    borderWidth: 1,
    padding: 5
  }
})